import { useState, useEffect, useCallback } from 'react';
import MuiDialog from 'components/MuiDialog';
import { Typography, Grid } from '@mui/material';
import { useForm } from 'react-hook-form';
import { SurveyDialogProps, TSimulationPlan } from 'routes/Main/Survey/type';
import useSurvey from 'store/survey/useSurvey';
import useNotification from 'hooks/useNotification';
import SimulPatientInfo from '../SimulPatientInfo';
import SectionTitle from '../../components/SectionTitle';
import MuiTextField from 'components/Form/MuiTextField';
import ActivityDetail from './ActivityDetail';
import { getSimulationPlan, updateSimulationPlan } from 'apis/survey';

const NursingActivityPlan = (props: SurveyDialogProps<TSimulationPlan>) => {
  const {
    title,
    isOpen,
    disabled,
    defaultValues,
    user_id,
    nurseName,
    patientInfo,
    onClose,
  } = props;
  const [activityPlan, setActivityPlan] = useState<any>();
  const { onUpdateIsSave } = useSurvey();
  const { onSuccess, onFail, onResultCode, onRequired } = useNotification();

  const { handleSubmit, register, getValues, setValue, watch, control, reset } =
    useForm({
      defaultValues,
    });

  const formProps = {
    disabled,
    watch,
    register,
    getValues,
    setValue,
    onSuccess,
    onRequired,
    activityPlan,
    nurseName,
  };

  useEffect(() => {
    if (activityPlan) {
      reset({
        sp_subject_name: activityPlan.sp_subject_name,
        sp_practice_date: activityPlan.sp_practice_date,
        sp_scenario: activityPlan.sp_scenario,
        sp_professor: activityPlan.sp_professor,
        sp_practice_group: activityPlan.sp_practice_group,
        sp_team_name: activityPlan.sp_team_name,
        sp_nurse1: activityPlan.sp_nurse1,
        sp_nurse2: activityPlan.sp_nurse2,
        sp_nurse3: activityPlan.sp_nurse3,
        sp_plan: activityPlan.sp_plan,
      });
    }
  }, [activityPlan, reset]);

  const onSubmit = (data: any) => {
    const request = {
      user_id,
      patient_id: patientInfo.patient_id,
      simulation_plan_info: { ...data },
    };
    updateSimulationPlan(request)
      .then(({ data: { rc } }) => {
        if (rc !== 1) {
          onResultCode(rc);
        } else {
          onUpdateIsSave(true);
          onSuccess('저장되었습니다');
        }
      })
      .catch(e => onFail('저장에 실패하였습니다', e));
  };

  const onGetActivityPlan = useCallback(() => {
    getSimulationPlan({
      patient_id: String(patientInfo.patient_id),
      user_id,
    })
      .then(({ data }) => {
        setActivityPlan(data.simulation_plan_info);
      })
      .catch((e: any) => {
        const message = `간호 활동계획서 정보를 호출하는데 실패했습니다.`;
        onFail(message, e);
      });
  }, []);

  useEffect(onGetActivityPlan, [onGetActivityPlan]);

  return (
    <MuiDialog.SurveyForm
      title={title}
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={disabled ? undefined : handleSubmit(onSubmit)}
      update_at={defaultValues?.update_at}
    >
      <Grid
        container
        wrap="wrap"
        rowSpacing={5}
        columnSpacing={3}
        sx={{ py: 5, px: 1 }}
      >
        <Typography
          sx={{
            margin: '40px auto 0px auto',
            fontWeight: '700',
            fontSize: '16px',
            textAlign: 'center',
          }}
        >
          시뮬레이션 간호 활동계획서
        </Typography>
        <ActivityDetail {...formProps} />
        <SimulPatientInfo patientInfo={patientInfo} nurseName={nurseName} />
      </Grid>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
        }}
      >
        <SectionTitle title="활동 계획서" />
        <MuiTextField
          sx={{
            marginTop: '20px',
            whiteSpace: 'pre',
          }}
          value={watch('sp_plan')}
          required={false}
          multiline
          minRows={15}
          {...register('sp_plan')}
          onPaste={e => e.preventDefault()}
          onKeyDown={e => {
            if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
              e.preventDefault();
            }
          }}
          onContextMenu={e => e.preventDefault()}
        />
      </div>
      <div style={{ width: '100%' }}>
        <span
          style={{
            color: '#14855B',
            float: 'right',
            marginTop: '10px',
            fontSize: '12px',
          }}
        >
          *해당 기록지는 삼육대학교 간호학과 협조를 통해 제작한 기록지입니다.
        </span>
      </div>
    </MuiDialog.SurveyForm>
  );
};

export default NursingActivityPlan;
